/**
 * NFC Solutions — Staff Sales (commission) panel. Session 30.27.
 *
 * Owner → Reports → Staff Sales. Per staff member: sales rung up since
 * the owner last reset them (the running commission figure), plus
 * today / this week / this month. Reset one person or everyone; the
 * figure being closed out is kept as "previous period" so the owner can
 * still see what was just paid.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RotateCcw, RefreshCw, Users, TrendingUp, Receipt, Clock3, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const money = (v) => {
  const n = Number(v || 0);
  const [whole, dec] = n.toFixed(2).split(".");
  return `R ${Number(whole).toLocaleString("en-US")}.${dec}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-ZA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return String(iso); }
};

const ROLE_LABEL = {
  owner: "Owner", manager: "Manager", shift_leader: "Shift leader", supervisor: "Supervisor",
  waiter: "Waiter", runner: "Runner", retailer: "Retail", kitchen: "Kitchen", bar: "Bar",
};

const WINDOWS = [
  { id: "since_reset", label: "Since reset" },
  { id: "today", label: "Today" },
  { id: "week", label: "This week" },
  { id: "month", label: "This month" },
];

export const StaffSalesPanel = ({ authHeaders, onSendToSpreadsheet }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [window_, setWindow] = useState("since_reset");
  const [showNet, setShowNet] = useState(false);
  const [confirm, setConfirm] = useState(null); // { user_key, name } | { all: true }
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    if (!authHeaders) return;
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/owner/analytics/staff-sales`, { headers: authHeaders });
      setData(r.data);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load staff sales."));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = Array.isArray(data?.staff) ? data.staff : [];
    // Deactivated logins with nothing on the board are noise — hide them.
    const hasAny = (r) => ["since_reset", "today", "week", "month"].some((k) => Number(r[k]?.orders || 0) > 0);
    const list = all.filter((r) => r.active !== false || hasAny(r));
    const key = window_ === "since_reset" ? "since_reset" : window_;
    return [...list].sort((a, b) => (Number(b[key]?.sales_total || 0) - Number(a[key]?.sales_total || 0)) || String(a.name).localeCompare(String(b.name)));
  }, [data, window_]);

  const totals = useMemo(() => {
    const key = window_;
    const acc = { orders: 0, items: 0, sales_total: 0, sales_net: 0 };
    for (const r of rows) {
      const b = r[key] || {};
      acc.orders += Number(b.orders || 0); acc.items += Number(b.items || 0);
      acc.sales_total += Number(b.sales_total || 0); acc.sales_net += Number(b.sales_net || 0);
    }
    return acc;
  }, [rows, window_]);

  const unattributed = data?.unattributed?.[window_ === "since_reset" ? "month" : window_] || null;

  const doReset = async () => {
    if (!confirm) return;
    setResetting(true);
    try {
      const body = confirm.all ? {} : { user_key: confirm.user_key };
      const r = await axios.post(`${API}/owner/analytics/staff-sales/reset`, body, { headers: authHeaders });
      toast.success(confirm.all
        ? `Reset ${r.data?.reset || 0} waiters — everyone starts from zero.`
        : `${confirm.name}'s sales counter reset to zero.`);
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset — please try again.");
    } finally {
      setResetting(false);
    }
  };

  const valueOf = (b) => (showNet ? b?.sales_net : b?.sales_total) || 0;

  return (
    <div className="space-y-4" data-testid="staff-sales-panel">
      <Card className="rounded-3xl border-border">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Users className="h-5 w-5" /> Staff Sales <Badge variant="outline" className="ml-1 text-[10px] uppercase">Waiters</Badge>
            </CardTitle>
            <CardDescription>
              Waiter sales only — every order each waiter rings up. Use the <strong>Since reset</strong> figure for commissions, then reset when you have paid out. Managers, supervisors and runners are not included.
              {data?.windows?.timezone ? ` Days follow ${data.windows.timezone}.` : ""}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {typeof onSendToSpreadsheet === "function" ? (
              <Button
                variant="outline"
                size="sm"
                disabled={loading || rows.length === 0}
                onClick={() => {
                  // Session 30.40 — hand the visible table to Reports → Spreadsheets.
                  const label = (WINDOWS.find((w) => w.id === window_) || {}).label || window_;
                  const header = ["Staff member", "Role", `Sales (${showNet ? "net" : "gross"})`, "Orders", "Items", "Counting since"];
                  const body = rows.map((r) => {
                    const b = r[window_] || {};
                    return [r.name, ROLE_LABEL[r.role] || r.role || "", Number(valueOf(b)) || 0, Number(b.orders || 0), Number(b.items || 0), r.reset_at ? new Date(r.reset_at).toLocaleDateString() : "Never reset"];
                  });
                  const n = body.length;
                  const total = ["TOTAL", "", { f: `=SUM(C2:C${n + 1})`, v: Number(showNet ? totals.sales_net : totals.sales_total) || 0, bl: true }, { f: `=SUM(D2:D${n + 1})`, v: totals.orders, bl: true }, { f: `=SUM(E2:E${n + 1})`, v: totals.items, bl: true }, ""];
                  onSendToSpreadsheet(`Staff sales — ${label} — ${new Date().toLocaleDateString()}`, [header, ...body, total], "staff_sales");
                }}
                data-testid="staff-sales-to-spreadsheet"
              >
                <FileSpreadsheet className="h-4 w-4" /> Send to spreadsheet
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="staff-sales-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              size="sm"
              className="bg-[hsl(var(--primary))] text-primary-foreground"
              onClick={() => setConfirm({ all: true })}
              disabled={loading || rows.length === 0}
              data-testid="staff-sales-reset-all"
            >
              <RotateCcw className="h-4 w-4" /> Reset all
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs value={window_} onValueChange={setWindow} className="w-full sm:w-auto">
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:inline-flex sm:w-auto" data-testid="staff-sales-window-tabs">
                {WINDOWS.map((w) => (
                  <TabsTrigger key={w.id} value={w.id} className="w-full sm:w-auto" data-testid={`staff-sales-window-${w.id}`}>{w.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <button
              type="button"
              onClick={() => setShowNet((v) => !v)}
              className="rounded-xl border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary"
              data-testid="staff-sales-toggle-net"
            >
              Showing {showNet ? "excl. VAT (net)" : "incl. VAT (gross)"} — switch
            </button>
          </div>

          {/* Totals strip */}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-border bg-secondary/40 p-4" data-testid="staff-sales-total-sales">
              <p className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Waiter sales</p>
              <p className="mt-1 font-['Space_Grotesk'] text-2xl font-semibold">{money(showNet ? totals.sales_net : totals.sales_total)}</p>
            </div>
            <div className="rounded-2xl border border-border bg-secondary/40 p-4" data-testid="staff-sales-total-orders">
              <p className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"><Receipt className="h-3.5 w-3.5" /> Orders rung up</p>
              <p className="mt-1 font-['Space_Grotesk'] text-2xl font-semibold">{totals.orders.toLocaleString("en-US")}</p>
            </div>
            <div className="rounded-2xl border border-border bg-secondary/40 p-4" data-testid="staff-sales-total-items">
              <p className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"><Clock3 className="h-3.5 w-3.5" /> Items sold</p>
              <p className="mt-1 font-['Space_Grotesk'] text-2xl font-semibold">{totals.items.toLocaleString("en-US")}</p>
            </div>
          </div>

          {error ? (
            <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm" data-testid="staff-sales-error">
              {error} <Button variant="link" size="sm" onClick={load}>Retry</Button>
            </div>
          ) : null}

          {loading && !data ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full rounded-2xl" />)}</div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="staff-sales-empty">
              No waiters yet. Add waiter logins under Operations → Staff &amp; Access; every order they ring up will be counted here.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm" data-testid="staff-sales-table">
                <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Staff member</th>
                    <th className="px-4 py-3 text-right">Sales</th>
                    <th className="px-4 py-3 text-right">Orders</th>
                    <th className="px-4 py-3 text-right">Items</th>
                    <th className="px-4 py-3">Counting since</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const b = r[window_] || {};
                    const prev = r.previous_period;
                    return (
                      <tr key={r.user_key} className="border-t border-border align-top hover:bg-secondary/30" data-testid={`staff-sales-row-${r.user_key}`}>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold" data-testid={`staff-sales-name-${r.user_key}`}>{r.name}</span>
                            {r.role ? <Badge variant="secondary" className="text-[10px] uppercase">{ROLE_LABEL[r.role] || r.role}</Badge> : null}
                            {r.active === false ? <Badge variant="outline" className="text-[10px]">inactive</Badge> : null}
                          </div>
                          {r.last_order_at ? <p className="mt-0.5 text-xs text-muted-foreground">Last order {fmtDate(r.last_order_at)}</p> : null}
                        </td>
                        <td className="px-4 py-3 text-right font-['Space_Grotesk'] text-base font-semibold" data-testid={`staff-sales-amount-${r.user_key}`}>{money(valueOf(b))}</td>
                        <td className="px-4 py-3 text-right" data-testid={`staff-sales-orders-${r.user_key}`}>{Number(b.orders || 0)}</td>
                        <td className="px-4 py-3 text-right">{Number(b.items || 0)}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {window_ === "since_reset" ? (
                            r.reset_at ? (
                              <>
                                <div>Reset {fmtDate(r.reset_at)}{r.reset_by ? ` by ${r.reset_by}` : ""}</div>
                                {prev ? <div className="mt-0.5" data-testid={`staff-sales-previous-${r.user_key}`}>Previous period: {money(showNet ? prev.sales_net : prev.sales_total)} · {prev.orders} orders</div> : null}
                              </>
                            ) : "Never reset — all-time"
                          ) : (
                            window_ === "today" ? "Since midnight" : window_ === "week" ? "Since Monday" : "Since the 1st"
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirm({ user_key: r.user_key, name: r.name })}
                            disabled={resetting}
                            data-testid={`staff-sales-reset-${r.user_key}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" /> Reset
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {unattributed && (unattributed.orders > 0) ? (
            <p className="text-xs text-muted-foreground" data-testid="staff-sales-unattributed">
              Not counted above ({window_ === "since_reset" ? "this month" : WINDOWS.find((w) => w.id === window_)?.label.toLowerCase()}): {money(showNet ? unattributed.sales_net : unattributed.sales_total)} across {unattributed.orders} orders placed by customers (QR menu), by non-waiter roles, or by shared logins without a waiter PIN.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent data-testid="staff-sales-reset-dialog">
          <DialogHeader>
            <DialogTitle>{confirm?.all ? "Reset everyone's sales counter?" : `Reset ${confirm?.name}'s sales counter?`}</DialogTitle>
            <DialogDescription>
              {confirm?.all
                ? "Every waiter's \"Since reset\" figure goes back to zero from now. Today / week / month totals are not affected, and the closed-out figures stay visible as \"Previous period\"."
                : "Their \"Since reset\" figure goes back to zero from now — do this once you have paid out the commission. Today / week / month totals are not affected; the closed-out figure stays visible as \"Previous period\"."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={resetting} data-testid="staff-sales-reset-cancel">Cancel</Button>
            <Button onClick={doReset} disabled={resetting} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="staff-sales-reset-confirm">
              <RotateCcw className="h-4 w-4" /> {resetting ? "Resetting…" : "Yes, reset to zero"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffSalesPanel;
